import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import { Input, Select, Text } from "components/base/Form";
import { Alert } from "components/base/Toast";
import InlineCustomerSelector from "components/shared/InlineCustomerSelector";
import InlineStockCategorySelector from "components/shared/InlineStockCategorySelector";
import { useState } from "react";
import { FETCH_CUSTOMERS_FOR_FCST, SAVE_FORECAST_CATEGORY } from "./graphql";

const ForecastCategoryFormView = ({ category, onCompleted }) => {
  const isEditMode = Boolean(category?.id);
  const [name, setName] = useState(category?.name || "");
  const [connectionType, setConnectionType] = useState(
    category?.connectionType || "NO",
  );
  const [customer, setCustomer] = useState(category?.customer || null);
  const [stockCategory, setStockCategory] = useState(
    category?.stockCategory || null,
  );

  const [description, setDescription] = useState(category?.description || "");
  const [saveForecastCategory, saveRes] = useMutation(SAVE_FORECAST_CATEGORY, {
    onCompleted(res) {
      const nextCategory = res?.saveForecastCategory?.forecastCategory;
      if (!nextCategory) return;
      Alert("success", isEditMode ? "Category updated." : "Category created.");
      onCompleted?.(nextCategory);
    },
    onError(error) {
      window.alert(parseError(error));
    },
    refetchQueries: ["FETCH_FORECAST_CATEGORY_AND_SALES_INFO"],
  });

  function saveCategory() {
    const normalizedName = name.trim();

    if (connectionType === "NO" && !normalizedName) {
      window.alert("Name is required.");
      return;
    }

    saveForecastCategory({
      variables: {
        id: category?.id,
        name: normalizedName || null,
        description: description.trim() || null,
        connectionType,
        customerId: connectionType === "CUSTOMER" ? customer?.id : null,
        stockCategoryId:
          connectionType === "STOCK_CATEGORY" ? stockCategory?.id : null,
      },
    });
  }

  return (
    <div className="text-sm">
      <div className="-mx-2">
        <table>
          <tbody>
            <tr>
              <td className=" align-baseline">
                <label>Connection Type: </label>
              </td>
              <td className="align-baseline">
                <Select
                  value={connectionType}
                  onChange={(e) => setConnectionType(e.target.value)}
                  disabled={saveRes.loading}
                >
                  <option value="NO">Manual</option>
                  <option value="CUSTOMER">Customer</option>
                  <option value="STOCK_CATEGORY">Warehouse</option>
                </Select>

                <div className="mt-1 mx-1 opacity-70 text-xs">
                  Choose a connection type will help wis to automatically link
                  the forecast category to a customer or stock category. Wis
                  will prepare sales or inventory data when you submit the
                  forecast for this category. You can also choose "No
                  Connection" if you want to manually enter the sales data.
                </div>
              </td>
            </tr>

            {connectionType === "CUSTOMER" && (
              <tr>
                <td>
                  <label>Customer: </label>
                </td>
                <td>
                  <InlineCustomerSelector
                    query={FETCH_CUSTOMERS_FOR_FCST}
                    valueView={<div>{customer?.name || " - "}</div>}
                    onChange={setCustomer}
                  />
                </td>
              </tr>
            )}

            {connectionType === "STOCK_CATEGORY" && (
              <tr>
                <td>
                  <label>Stock Category: </label>
                </td>
                <td>
                  <InlineStockCategorySelector
                    valueView={<div>{stockCategory?.name || " - "}</div>}
                    onChange={setStockCategory}
                  />
                </td>
              </tr>
            )}

            {connectionType === "NO" && (
              <tr>
                <td>
                  <label>Name: </label>
                </td>
                <td className="w-full">
                  <Input
                    className="w-full"
                    placeholder="Give a name for reference."
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </td>
              </tr>
            )}

            <tr>
              <td>
                <label>Note: </label>
              </td>
              <td>
                <Text
                  placeholder="Optional note for this category"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="pt-2 flex justify-end">
        <Button primary onClick={saveCategory} loading={saveRes.loading}>
          {isEditMode ? "Save" : "Create"}
        </Button>
      </div>
    </div>
  );
};

export default ForecastCategoryFormView;
